import axiosInstance from 'shared/services/api/axiosInstance';

function teamId() {
  try {
    const raw = localStorage.getItem('selectedTeamData');
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed?.id ?? parsed?._id ?? localStorage.getItem('selectedTeam') ?? null;
  } catch (_) {
    return localStorage.getItem('selectedTeam');
  }
}
const params = (extra = {}) => ({ params: { team_id: teamId(), ...extra } });

export async function getAlertCatalog() {
  const { data } = await axiosInstance.get('/alerts/catalog');
  return data;
}
export async function getAlertSchema(scope) {
  const { data } = await axiosInstance.get('/alerts/schema', params({ scope }));
  return data;
}
/** Values a field has held, to suggest in the picker. Suggestions only. */
export async function getAlertFieldValues(scope, field, evalLineageId = null, { q, limit, signal } = {}) {
  const { data } = await axiosInstance.get('/alerts/field-values', {
    ...params({ scope, field, ...(evalLineageId ? { eval_lineage_id: evalLineageId } : {}), ...(q ? { q } : {}), ...(limit ? { limit } : {}) }),
    ...(signal ? { signal } : {}),
  });
  return data.values || [];
}
export async function listAlerts(q = '') {
  const { data } = await axiosInstance.get('/alerts', params(q ? { q } : {}));
  return data.alerts || [];
}
export async function getAlert(id) {
  const { data } = await axiosInstance.get(`/alerts/${id}`, params());
  return data.alert;
}
export async function createAlert(body) {
  const { data } = await axiosInstance.post('/alerts', { ...body, team_id: teamId() });
  return data.alert;
}
export async function updateAlert(id, body) {
  const { data } = await axiosInstance.put(`/alerts/${id}`, { ...body, team_id: teamId() });
  return data.alert;
}
export async function setAlertEnabled(id, enabled) {
  const { data } = await axiosInstance.post(`/alerts/${id}/enabled`, { enabled, team_id: teamId() });
  return data.alert;
}
export async function deleteAlert(id) {
  await axiosInstance.delete(`/alerts/${id}`, params());
}
export async function previewAlert(body) {
  const { data } = await axiosInstance.post('/alerts/preview', { ...body, team_id: teamId() });
  return data;
}
/** `range` is { from, to } as ISO strings; without one the last `hours` (default 24). */
function viewerZone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined; } catch (_) { return undefined; }
}
/** Readings are kept this many days; nothing older exists to ask for. */
export const ALERT_READING_DAYS = 30;
export async function getAlertSeries(id, range = 24) {
  let extra = { hours: range };
  if (typeof range === 'object' && range) {
    const floor = Date.now() - ALERT_READING_DAYS * 86400000;
    extra = { from: Date.parse(range.from) < floor ? new Date(floor).toISOString() : range.from, to: range.to };
  }
  const { data } = await axiosInstance.get(`/alerts/${id}/series`, params(extra));
  return data.series || [];
}
export async function getAlertStats(id, range = null) {
  const { data } = await axiosInstance.get(`/alerts/${id}/stats`, params(range ? { from: range.from, to: range.to, tz: viewerZone() } : {}));
  return data;
}
/** One page of fires, newest first, with how many there are in all. `offset` asks for the next page. */
export async function listAlertEvents(id, range = null, offset = 0) {
  const { data } = await axiosInstance.get(`/alerts/${id}/events`, params({
    ...(range ? { from: range.from, to: range.to, limit: 200 } : {}),
    ...(offset ? { offset } : {}),
  }));
  const events = data.events || [];
  return { events, total: data.total ?? events.length };
}
/** Starting points for a new alert and the categories they sit in (services/alerts/templates.js). */
export async function listAlertTemplates() {
  const { data } = await axiosInstance.get('/alerts/templates');
  return { templates: data?.templates || [], categories: data?.categories || [] };
}
/** One template built into an unsaved alert body, narrowed by agent / tool / error_type. */
export async function buildAlertTemplate(id, narrow = {}) {
  const { data } = await axiosInstance.get(`/alerts/templates/${encodeURIComponent(id)}`, { params: narrow });
  return data;
}
